import { defineComponent, inject, computed, ref, createElementBlock, openBlock, createElementVNode, withDirectives, createCommentVNode, createVNode, createBlock, unref, normalizeClass, toDisplayString, withCtx, Fragment, renderList, isRef, vShow, createTextVNode } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "@nuxt/ui/composables/useToast";
import { addKontyToast } from "app-theme";
import UButton from "@nuxt/ui/components/Button.vue";
import UTabs from "@nuxt/ui/components/Tabs.vue";
import UCheckbox from "@nuxt/ui/components/Checkbox.vue";
import UFormField from "@nuxt/ui/components/FormField.vue";
import UInput from "@nuxt/ui/components/Input.vue";
import USelect from "@nuxt/ui/components/Select.vue";
import USelectMenu from "@nuxt/ui/components/SelectMenu.vue";
import UTextarea from "@nuxt/ui/components/Textarea.vue";
import _sfc_main$2 from "./index31.js";
import UAlert from "@nuxt/ui/components/Alert.vue";
import USeparator from "@nuxt/ui/components/Separator.vue";
import { maskito } from "@maskito/vue";
import { Store, ProductCategory, TaxRate, Money } from "app-core";
import { useMaskito } from "./index51.js";
import { useSheetLayer, useSheetLayerGuard, SHEET_LEADING_CONTROL_CLASS } from "./index48.js";
import { useProductForm } from "./index41.js";
import _sfc_main$1 from "./index29.js";
const _hoisted_1 = { class: "flex min-h-0 flex-1 flex-col" };
const _hoisted_2 = { class: "flex h-15 shrink-0 items-center gap-2 px-2" };
const _hoisted_3 = { class: "text-panel-title text-highlighted" };
const _hoisted_4 = { class: "min-h-0 flex-1 overflow-y-auto p-4" };
const _hoisted_5 = {
  key: 0,
  class: "mb-6 mt-1 space-y-4"
};
const _hoisted_6 = { class: "grid grid-cols-12 gap-4" };
const _hoisted_7 = {
  key: 1,
  class: "my-6"
};
const _hoisted_8 = { class: "text-section-label text-muted mx-1 mb-2 mt-6" };
const _hoisted_9 = { class: "grid grid-cols-12 gap-4" };
const _hoisted_10 = {
  key: 2,
  class: "my-6"
};
const _hoisted_11 = { class: "text-section-label text-muted mx-1 mb-2 mt-6" };
const _hoisted_12 = { class: "grid grid-cols-12 gap-3" };
const _hoisted_13 = {
  key: 2,
  class: "col-span-12"
};
const _hoisted_14 = {
  key: 0,
  class: "min-h-0 flex-1 overflow-y-auto p-4"
};
const _hoisted_15 = { class: "mb-2" };
const _hoisted_16 = { class: "mt-3 grid grid-cols-12 items-end gap-3" };
const _hoisted_17 = { class: "col-span-3 flex h-9 items-center break-all" };
const _hoisted_18 = { key: 0 };
const _hoisted_19 = { class: "col-span-3 flex justify-end" };
const _hoisted_20 = { class: "pt-3 pb-2 text-primary" };
const _hoisted_21 = { class: "min-w-0 flex-1 truncate" };
const _hoisted_22 = { class: "text-dimmed" };
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "ProductForm",
  props: {
    product: {
      type: Object,
      default: () => void 0
    },
    /** AD-4: the API prefix — POS omits it (`/products`), admin passes '/admin'. */
    endpointBase: {
      type: String,
      default: ""
    },
    /** Admin item editors pass 'inventoryAccounting' (service tab + is_made locked). */
    mode: {
      type: String,
      default: "default"
    },
    /** Full product list override (admin's server-fetched list) — default Product.all(). */
    products: {
      type: Array,
      default: () => void 0
    },
    /** Category options override — default ProductCategory.all(). */
    productCategories: {
      type: Array,
      default: () => void 0
    },
    /** Pricelist rows override — default Pricelist.all(). */
    pricelists: {
      type: Array,
      default: () => void 0
    },
    /** Receives the model instance + the raw server row (admin tables read relations off it). */
    onSaved: {
      type: Function,
      default: () => {
      }
    },
    onCancel: {
      type: Function,
      default: () => {
      }
    },
    /**
     * Navigation seam for hosts with their own ingredients surface (POS
     * CodSheet page): receives a handle whose `setIngredientsAndSave(pivots)`
     * completes the save. Hosts without it get the built-in step 2.
     */
    onOpenIngredients: {
      type: Function,
      default: void 0
    }
  },
  setup(__props, { expose: __expose }) {
    const props = __props;
    const { t } = useI18n();
    const { createNumberMask } = useMaskito();
    const priceMask = createNumberMask({ precision: 3, min: 0 });
    const quantityMask = createNumberMask({ precision: 3, min: 0 });
    const countMask = createNumberMask({ precision: 3 });
    const nuxtToast = useToast();
    const backoffice = inject("kontyBackoffice", false);
    const { nested } = useSheetLayer();
    useSheetLayerGuard();
    const footerClass = computed(
      () => "flex min-h-17 shrink-0 items-center gap-2 px-4 py-3 *:flex-1" + (backoffice ? " sm:justify-end sm:*:flex-none" : "")
    );
    const buttonSize = backoffice ? "md" : "lg";
    const toast = inject("kontyToast", (message, status) => {
      addKontyToast(nuxtToast, { description: message, color: status === "error" ? "error" : "success" });
    });
    const codConfirm = inject("codConfirm", void 0);
    const isRetail = !!Store.get("isRetail");
    const modules = Store.get("modules");
    const integrations = Store.get("integrations");
    const appConfig = Store.get("appConfig");
    const {
      form,
      netQuantity,
      unitsOfMeasure,
      createUnitOfMeasure,
      inventoryAccountingEnabled,
      showCount,
      showMinCount,
      showPurchasePrice,
      showPriceDifference,
      disableSellingPriceEdit,
      disablePurchasePrice,
      disablePriceDifference,
      showNetQuantity,
      netQuantityUnitOfMeasure,
      showUnitOfMeasureSold,
      showDigitalMenu,
      showDietaryTags,
      showDeliveryFields,
      availableIngredients,
      validateField,
      save
    } = useProductForm(props.product, {
      endpointBase: props.endpointBase,
      mode: props.mode,
      products: props.products,
      productCategories: props.productCategories,
      pricelists: props.pricelists
    });
    const step = ref(1);
    const typeTabs = computed(() => [
      { label: t("general.goods"), value: "goods" },
      { label: t("general.service"), value: "service" }
    ]);
    const categories = computed(() => props.productCategories ?? ProductCategory.all());
    const printingSectorItems = computed(() => {
      var _a;
      return (((_a = appConfig == null ? void 0 : appConfig.product) == null ? void 0 : _a.printingSectors) || []).map((sector) => ({
        label: t(`products.printing_sector.${sector}`),
        value: sector
      }));
    });
    const taxRateItems = computed(() => TaxRate.productTaxRates().map((taxRate) => ({
      label: taxRate.name ? `${taxRate.name} (${taxRate.label}) - ${taxRate.rate}%` : `${taxRate.label} - ${taxRate.rate}%`,
      value: taxRate.id
    })));
    const DIETARY_CODES = [
      "gluten",
      "crustaceans",
      "eggs",
      "fish",
      "peanuts",
      "soybeans",
      "milk",
      "nuts",
      "celery",
      "mustard",
      "sesame",
      "sulphites",
      "lupin",
      "molluscs",
      "vegetarian",
      "vegan",
      "gluten_free"
    ];
    const dietaryItems = computed(() => DIETARY_CODES.map((code) => ({
      label: t(`products.dietary.${code}`),
      value: code
    })));
    const descriptionText = computed({
      get: () => form.data.description ?? "",
      set: (value) => {
        form.data.description = value.trim() ? value : null;
      }
    });
    const imageUrlText = computed({
      get: () => form.data.image_url ?? "",
      set: (value) => {
        form.data.image_url = value.trim() ? value.trim() : null;
      }
    });
    const dietaryTags = computed({
      get: () => form.data.dietary_tags ?? [],
      set: (value) => {
        form.data.dietary_tags = value.length ? value : null;
      }
    });
    const unitSoldItems = computed(() => unitsOfMeasure.value.map((unit) => ({
      label: unit.value,
      value: unit.key
    })));
    const priceLabel = computed(() => {
      var _a, _b;
      const label = t("general.price");
      const reverseCalculation = !!((_b = (_a = Store.get("tenant")) == null ? void 0 : _a.venue) == null ? void 0 : _b.settings.reverse_calculation);
      const suffix = reverseCalculation ? t("general.pre_tax") : t("general.after_tax");
      return label + " " + suffix.charAt(0).toLowerCase() + suffix.slice(1);
    });
    function errText(field) {
      const messages = form.errors.value.filter((e) => e.name === field).map((e) => e.message);
      return messages.length ? messages[0] : void 0;
    }
    function onTypeChange(value) {
      if (props.mode === "inventoryAccounting") return;
      form.data.is_goods = value === "goods";
    }
    function onPrintingSectorChange(value) {
      form.data.printing_sector = value;
      validateField("printing_sector");
    }
    function onTaxRateChange(value) {
      form.data.tax_rate_id = value;
      validateField("tax_rate_id");
    }
    function onUnitSoldChange(value) {
      form.data.unit_of_measure_sold = value;
      validateField("unit_of_measure_sold");
    }
    function createCategory(name) {
      if (name.length < 3) return;
      if (isRetail) {
        form.data.category = { name, type: null };
        return;
      }
      if (!codConfirm) {
        form.data.category = { name, type: null };
        return;
      }
      codConfirm({
        title: t("general.category") + ": " + name,
        message: t("products.new_category_message_choose_type"),
        canDismiss: true,
        buttonTrueColor: "success",
        buttonTrueText: t("general.save"),
        inputs: [{
          type: "select",
          key: "type",
          label: t("general.type"),
          itemText: "name",
          items: ((appConfig == null ? void 0 : appConfig.product.types) || []).map((i) => ({ key: i, name: i })),
          filter: (item, query) => item.key.toLowerCase().includes(query.toLocaleLowerCase()),
          validation: "required"
        }],
        yesHandler: async (values) => {
          form.data.category = { name, type: values.type.key };
        }
      });
    }
    async function submit() {
      const result = await save();
      if (result.status === "offline") {
        toast(t("connection.internet_offline_message_not_allowed"), "error");
        return;
      }
      if (result.status === "in_use") {
        toast(t("products.has_super_products"), "error");
        return;
      }
      if (result.status === "saved") {
        toast(t("products.inventory_item_saved"), "success");
        props.onSaved(result.product, result.raw);
      }
      if (result.status === "error" && !form.errors.value.length) {
        toast(t("general.error_message"), "error");
      }
    }
    function openIngredients() {
      if (form.validate().length) return;
      if (props.onOpenIngredients) {
        props.onOpenIngredients({
          setIngredientsAndSave: async (pivots) => {
            form.data.ingredients = pivots;
            await submit();
          }
        });
        return;
      }
      step.value = 2;
    }
    function initialIngredients() {
      var _a;
      const rows = ((_a = props.product) == null ? void 0 : _a.ingredients) || [];
      return rows.filter((row) => row.pivot && row.pivot.product_id != null).map((row) => ({
        product_id: row.pivot.product_id,
        name: row.name || "",
        unit: row.unit_of_measure || "",
        quantity: Number(row.pivot.quantity) || 0
      }));
    }
    const selectedIngredients = ref(initialIngredients());
    const ingredientToAdd = ref(void 0);
    const ingredientQuantity = ref("1");
    const ingredientMultiplier = ref("1");
    const ingredientsAlert = ref(true);
    const ingredientTotal = computed(() => {
      const total = (parseFloat(ingredientQuantity.value) || 0) * (parseFloat(ingredientMultiplier.value) || 0);
      return total > 0 ? total : 0;
    });
    const selectableIngredients = computed(() => availableIngredients.value.filter((p) => !selectedIngredients.value.some((i) => i.product_id === p.id)));
    function addIngredient() {
      if (!ingredientToAdd.value || !ingredientTotal.value) return;
      selectedIngredients.value.push({
        product_id: ingredientToAdd.value.id,
        name: ingredientToAdd.value.name || "",
        unit: ingredientToAdd.value.unit_of_measure || "",
        quantity: Money.parse(ingredientTotal.value).toRoundedUnit(3)
      });
      ingredientToAdd.value = void 0;
      ingredientQuantity.value = "1";
      ingredientMultiplier.value = "1";
    }
    function removeIngredient(index) {
      selectedIngredients.value.splice(index, 1);
    }
    async function submitWithIngredients() {
      form.data.ingredients = selectedIngredients.value.map((i) => ({ product_id: i.product_id, quantity: i.quantity }));
      await submit();
    }
    const isMade = computed(() => form.data.is_made);
    __expose({ submit, openIngredients, busy: form.busy, step, isMade });
    return (_ctx, _cache) => {
      var _a, _b, _c;
      return openBlock(), createElementBlock("div", _hoisted_1, [
        createElementVNode("div", _hoisted_2, [
          createVNode(UButton, {
            color: "neutral",
            variant: "ghost",
            size: "md",
            class: normalizeClass(unref(backoffice) ? void 0 : unref(SHEET_LEADING_CONTROL_CLASS)),
            icon: step.value === 2 || unref(nested) ? "i-lucide-chevron-left" : "i-lucide-x",
            "aria-label": step.value === 2 || unref(nested) ? unref(t)("general.back") : unref(t)("general.close"),
            onClick: _cache[0] || (_cache[0] = ($event) => step.value === 2 ? step.value = 1 : __props.onCancel())
          }, null, 8, ["class", "icon", "aria-label"]),
          createElementVNode("span", _hoisted_3, toDisplayString(unref(t)("general.product", 1)), 1),
          _cache[42] || (_cache[42] = createElementVNode("div", { class: "flex-1" }, null, -1)),
          step.value === 1 ? (openBlock(), createBlock(UTabs, {
            key: 0,
            class: "mr-2 w-56",
            content: false,
            items: typeTabs.value,
            "model-value": unref(form).data.is_goods ? "goods" : "service",
            "onUpdate:modelValue": onTypeChange
          }, null, 8, ["items", "model-value"])) : createCommentVNode("", true)
        ]),
        withDirectives(createElementVNode("div", _hoisted_4, [
          ((_a = unref(modules)) == null ? void 0 : _a.product_ingredients) && unref(form).data.is_goods && !isRetail ? (openBlock(), createElementBlock("div", _hoisted_5, [
            createVNode(UCheckbox, {
              modelValue: unref(form).data.is_ingredient,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => unref(form).data.is_ingredient = $event),
              label: unref(t)("products.is_ingredient"),
              description: unref(t)("products.is_ingredient_description")
            }, null, 8, ["modelValue", "label", "description"]),
            createVNode(UCheckbox, {
              modelValue: unref(form).data.is_made,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => unref(form).data.is_made = $event),
              disabled: __props.mode === "inventoryAccounting",
              label: unref(t)("products.is_made"),
              description: unref(t)("products.is_made_description")
            }, null, 8, ["modelValue", "disabled", "label", "description"])
          ])) : createCommentVNode("", true),
          createElementVNode("div", _hoisted_6, [
            createVNode(UFormField, {
              class: "col-span-12 sm:col-span-6",
              label: unref(t)("general.name"),
              error: errText("name")
            }, {
              default: withCtx(() => [
                createVNode(UInput, {
                  modelValue: unref(form).data.name,
                  "onUpdate:modelValue": [
                    _cache[3] || (_cache[3] = ($event) => unref(form).data.name = $event),
                    _cache[4] || (_cache[4] = ($event) => unref(form).clearErrors("name"))
                  ],
                  class: "w-full",
                  onBlur: _cache[5] || (_cache[5] = ($event) => unref(validateField)("name"))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }, 8, ["label", "error"]),
            createVNode(UFormField, {
              class: "col-span-12 sm:col-span-6",
              label: unref(t)("general.code"),
              error: errText("code")
            }, {
              default: withCtx(() => [
                createVNode(UInput, {
                  modelValue: unref(form).data.code,
                  "onUpdate:modelValue": [
                    _cache[6] || (_cache[6] = ($event) => unref(form).data.code = $event),
                    _cache[7] || (_cache[7] = ($event) => unref(form).clearErrors("code"))
                  ],
                  class: "w-full",
                  onBlur: _cache[8] || (_cache[8] = ($event) => unref(validateField)("code"))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }, 8, ["label", "error"]),
            unref(form).data.is_goods ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
              createVNode(UFormField, {
                class: "col-span-12 sm:col-span-6",
                label: unref(t)("general.barcode"),
                error: errText("barcode")
              }, {
                default: withCtx(() => [
                  createVNode(UInput, {
                    modelValue: unref(form).data.barcode,
                    "onUpdate:modelValue": [
                      _cache[9] || (_cache[9] = ($event) => unref(form).data.barcode = $event),
                      _cache[10] || (_cache[10] = ($event) => unref(form).clearErrors("barcode"))
                    ],
                    class: "w-full",
                    onBlur: _cache[11] || (_cache[11] = ($event) => unref(validateField)("barcode"))
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }, 8, ["label", "error"]),
              createVNode(UFormField, {
                class: "col-span-12 sm:col-span-6",
                label: unref(t)("general.gtin"),
                error: errText("gtin")
              }, {
                default: withCtx(() => [
                  createVNode(UInput, {
                    modelValue: unref(form).data.gtin,
                    "onUpdate:modelValue": [
                      _cache[12] || (_cache[12] = ($event) => unref(form).data.gtin = $event),
                      _cache[13] || (_cache[13] = ($event) => unref(form).clearErrors("gtin"))
                    ],
                    class: "w-full",
                    onBlur: _cache[14] || (_cache[14] = ($event) => unref(validateField)("gtin"))
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }, 8, ["label", "error"])
            ], 64)) : createCommentVNode("", true),
            createVNode(UFormField, {
              class: normalizeClass(["col-span-12", !isRetail ? "sm:col-span-6" : "sm:col-span-12"]),
              label: unref(t)("general.category"),
              error: errText("category"),
              help: unref(form).data.category && !unref(form).data.category.id ? unref(t)("products.new_category_message") : void 0
            }, {
              default: withCtx(() => [
                createVNode(_sfc_main$1, {
                  modelValue: unref(form).data.category,
                  "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event) => unref(form).data.category = $event),
                  items: categories.value,
                  "item-key": "id",
                  "item-text": "name",
                  "return-object": "",
                  "can-create": (q) => q.length >= 3,
                  "create-label": (q) => unref(t)("products.no_category_message_alt", { value: q }),
                  onCreate: createCategory
                }, null, 8, ["modelValue", "items", "can-create", "create-label"])
              ]),
              _: 1
            }, 8, ["class", "label", "error", "help"]),
            !isRetail ? (openBlock(), createBlock(UFormField, {
              key: 1,
              class: "col-span-12 sm:col-span-6",
              label: unref(t)("products.print_to"),
              error: errText("printing_sector")
            }, {
              default: withCtx(() => [
                createVNode(USelect, {
                  "model-value": unref(form).data.printing_sector,
                  items: printingSectorItems.value,
                  class: "w-full",
                  "onUpdate:modelValue": onPrintingSectorChange
                }, null, 8, ["model-value", "items"])
              ]),
              _: 1
            }, 8, ["label", "error"])) : createCommentVNode("", true)
          ]),
          unref(form).data.is_goods && ((_b = unref(integrations)) == null ? void 0 : _b.scales) ? (openBlock(), createElementBlock("div", _hoisted_7, [
            createVNode(UCheckbox, {
              modelValue: unref(form).data.is_measured,
              "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event) => unref(form).data.is_measured = $event),
              label: unref(t)("products.is_measured"),
              description: unref(t)("products.is_measured_description")
            }, null, 8, ["modelValue", "label", "description"])
          ])) : createCommentVNode("", true),
          createElementVNode("h4", _hoisted_8, toDisplayString(unref(t)("products.price_and_stock")), 1),
          createElementVNode("div", _hoisted_9, [
            createVNode(UFormField, {
              class: "col-span-12 sm:col-span-3",
              label: priceLabel.value,
              error: errText("selling_price")
            }, {
              default: withCtx(() => [
                withDirectives(createVNode(UInput, {
                  modelValue: unref(form).data.selling_price,
                  "onUpdate:modelValue": [
                    _cache[17] || (_cache[17] = ($event) => unref(form).data.selling_price = $event),
                    _cache[18] || (_cache[18] = ($event) => unref(form).clearErrors("selling_price"))
                  ],
                  class: "w-full",
                  inputmode: "decimal",
                  disabled: unref(disableSellingPriceEdit),
                  onBlur: _cache[19] || (_cache[19] = ($event) => unref(validateField)("selling_price"))
                }, null, 8, ["modelValue", "disabled"]), [
                  [unref(maskito), unref(priceMask)]
                ])
              ]),
              _: 1
            }, 8, ["label", "error"]),
            unref(showPurchasePrice) ? (openBlock(), createBlock(UFormField, {
              key: 0,
              class: "col-span-12 sm:col-span-3",
              label: unref(t)("products.purchase_price"),
              error: errText("purchase_price")
            }, {
              default: withCtx(() => [
                withDirectives(createVNode(UInput, {
                  "model-value": unref(form).data.purchase_price ?? void 0,
                  class: "w-full",
                  inputmode: "decimal",
                  disabled: unref(disablePurchasePrice),
                  "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event) => {
                    unref(form).data.purchase_price = $event;
                    unref(form).clearErrors("purchase_price");
                  })
                }, null, 8, ["model-value", "disabled"]), [
                  [unref(maskito), unref(priceMask)]
                ])
              ]),
              _: 1
            }, 8, ["label", "error"])) : createCommentVNode("", true),
            unref(showPriceDifference) ? (openBlock(), createBlock(UFormField, {
              key: 1,
              class: "col-span-12 sm:col-span-3",
              label: unref(t)("products.price_difference"),
              error: errText("price_difference")
            }, {
              default: withCtx(() => [
                withDirectives(createVNode(UInput, {
                  "model-value": unref(form).data.price_difference ?? void 0,
                  class: "w-full",
                  inputmode: "decimal",
                  disabled: unref(disablePriceDifference),
                  "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event) => {
                    unref(form).data.price_difference = $event;
                    unref(form).clearErrors("price_difference");
                  })
                }, null, 8, ["model-value", "disabled"]), [
                  [unref(maskito), unref(priceMask)]
                ])
              ]),
              _: 1
            }, 8, ["label", "error"])) : createCommentVNode("", true),
            ((_c = unref(modules)) == null ? void 0 : _c.pricelists) ? (openBlock(true), createElementBlock(Fragment, { key: 2 }, renderList(unref(form).data.prices || [], (pricelist, index) => {
              return openBlock(), createBlock(UFormField, {
                key: "prics" + index,
                class: "col-span-12 sm:col-span-3",
                label: `${pricelist.name} ${unref(t)("general.price")}`,
                error: errText(`prices.${index}.price`)
              }, {
                default: withCtx(() => [
                  withDirectives(createVNode(UInput, {
                    modelValue: pricelist.price,
                    "onUpdate:modelValue": [($event) => pricelist.price = $event, ($event) => unref(form).clearErrors(`prices.${index}.price`)],
                    class: "w-full",
                    inputmode: "decimal",
                    disabled: unref(disableSellingPriceEdit),
                    onBlur: ($event) => unref(validateField)(`prices.${index}.price`)
                  }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled", "onBlur"]), [
                    [unref(maskito), unref(priceMask)]
                  ])
                ]),
                _: 2
              }, 1032, ["label", "error"]);
            }), 128)) : createCommentVNode("", true),
            createVNode(UFormField, {
              class: "col-span-12 sm:col-span-3",
              label: unref(t)("general.tax_rate"),
              error: errText("tax_rate_id")
            }, {
              default: withCtx(() => [
                createVNode(USelect, {
                  "model-value": unref(form).data.tax_rate_id,
                  items: taxRateItems.value,
                  class: "w-full",
                  "onUpdate:modelValue": onTaxRateChange
                }, null, 8, ["model-value", "items"])
              ]),
              _: 1
            }, 8, ["label", "error"]),
            unref(showCount) ? (openBlock(), createBlock(UFormField, {
              key: 3,
              class: "col-span-12 sm:col-span-3",
              label: unref(t)("general.in_stock"),
              error: errText("count")
            }, {
              default: withCtx(() => [
                withDirectives(createVNode(UInput, {
                  modelValue: unref(form).data.count,
                  "onUpdate:modelValue": [
                    _cache[22] || (_cache[22] = ($event) => unref(form).data.count = $event),
                    _cache[23] || (_cache[23] = ($event) => unref(form).clearErrors("count"))
                  ],
                  class: "w-full",
                  inputmode: "decimal",
                  disabled: unref(inventoryAccountingEnabled),
                  onBlur: _cache[24] || (_cache[24] = ($event) => unref(validateField)("count"))
                }, null, 8, ["modelValue", "disabled"]), [
                  [unref(maskito), unref(countMask)]
                ])
              ]),
              _: 1
            }, 8, ["label", "error"])) : createCommentVNode("", true),
            unref(showMinCount) ? (openBlock(), createBlock(UFormField, {
              key: 4,
              class: "col-span-12 sm:col-span-3",
              label: unref(t)("general.min_stock"),
              error: errText("min_count")
            }, {
              default: withCtx(() => [
                withDirectives(createVNode(UInput, {
                  "model-value": unref(form).data.min_count ?? void 0,
                  class: "w-full",
                  inputmode: "decimal",
                  "onUpdate:modelValue": _cache[25] || (_cache[25] = ($event) => {
                    unref(form).data.min_count = $event;
                    unref(form).clearErrors("min_count");
                  }),
                  onBlur: _cache[26] || (_cache[26] = ($event) => unref(validateField)("min_count"))
                }, null, 8, ["model-value"]), [
                  [unref(maskito), unref(quantityMask)]
                ])
              ]),
              _: 1
            }, 8, ["label", "error"])) : createCommentVNode("", true),
            createVNode(UFormField, {
              class: "col-span-12 sm:col-span-3",
              label: unref(t)("products.unit"),
              error: errText("unit_of_measure")
            }, {
              default: withCtx(() => [
                createVNode(_sfc_main$1, {
                  modelValue: unref(form).data.unit_of_measure,
                  "onUpdate:modelValue": _cache[27] || (_cache[27] = ($event) => unref(form).data.unit_of_measure = $event),
                  items: unref(unitsOfMeasure),
                  "item-value": "key",
                  "item-text": "value",
                  "item-key": "key",
                  "can-create": "",
                  "create-label": () => unref(t)("products.no_unit_message"),
                  onCreate: unref(createUnitOfMeasure)
                }, null, 8, ["modelValue", "items", "create-label", "onCreate"])
              ]),
              _: 1
            }, 8, ["label", "error"]),
            unref(showUnitOfMeasureSold) ? (openBlock(), createBlock(UFormField, {
              key: 5,
              class: "col-span-12 sm:col-span-3",
              label: unref(t)("products.unit_sold"),
              error: errText("unit_of_measure_sold")
            }, {
              default: withCtx(() => [
                createVNode(USelect, {
                  "model-value": unref(form).data.unit_of_measure_sold,
                  items: unitSoldItems.value,
                  class: "w-full",
                  "onUpdate:modelValue": onUnitSoldChange
                }, null, 8, ["model-value", "items"])
              ]),
              _: 1
            }, 8, ["label", "error"])) : createCommentVNode("", true),
            unref(showNetQuantity) ? (openBlock(), createBlock(UFormField, {
              key: 6,
              class: "col-span-12 sm:col-span-3",
              label: unref(t)("general.net_quantity_in", { value: unref(netQuantityUnitOfMeasure) }),
              error: errText("net_quantity")
            }, {
              default: withCtx(() => [
                withDirectives(createVNode(UInput, {
                  modelValue: unref(netQuantity),
                  "onUpdate:modelValue": [
                    _cache[28] || (_cache[28] = ($event) => isRef(netQuantity) ? netQuantity.value = $event : null),
                    _cache[29] || (_cache[29] = ($event) => unref(form).clearErrors("net_quantity"))
                  ],
                  class: "w-full",
                  inputmode: "decimal",
                  onBlur: _cache[30] || (_cache[30] = ($event) => unref(validateField)("net_quantity"))
                }, null, 8, ["modelValue"]), [
                  [unref(maskito), unref(quantityMask)]
                ])
              ]),
              _: 1
            }, 8, ["label", "error"])) : createCommentVNode("", true)
          ]),
          unref(form).data.is_goods && unref(form).data.is_sold ? (openBlock(), createElementBlock("div", _hoisted_10, [
            createVNode(UCheckbox, {
              modelValue: unref(form).data.requires_quantity,
              "onUpdate:modelValue": _cache[31] || (_cache[31] = ($event) => unref(form).data.requires_quantity = $event),
              label: unref(t)("products.requires_quantity"),
              description: unref(t)("products.requires_quantity_description")
            }, null, 8, ["modelValue", "label", "description"])
          ])) : createCommentVNode("", true),
          unref(showDigitalMenu) ? (openBlock(), createElementBlock(Fragment, { key: 3 }, [
            createElementVNode("h4", _hoisted_11, toDisplayString(unref(t)("products.digital_menu")), 1),
            createElementVNode("div", _hoisted_12, [
              createVNode(UFormField, {
                class: "col-span-12",
                label: unref(t)("products.description"),
                error: errText("description")
              }, {
                default: withCtx(() => [
                  createVNode(UTextarea, {
                    modelValue: descriptionText.value,
                    "onUpdate:modelValue": _cache[32] || (_cache[32] = ($event) => descriptionText.value = $event),
                    class: "w-full",
                    rows: 2,
                    maxlength: 2e3,
                    placeholder: unref(t)("products.description_placeholder")
                  }, null, 8, ["modelValue", "placeholder"])
                ]),
                _: 1
              }, 8, ["label", "error"]),
              createVNode(UFormField, {
                class: "col-span-12 sm:col-span-6",
                label: unref(t)("products.image_url"),
                error: errText("image_url")
              }, {
                default: withCtx(() => [
                  createVNode(UInput, {
                    modelValue: imageUrlText.value,
                    "onUpdate:modelValue": _cache[33] || (_cache[33] = ($event) => imageUrlText.value = $event),
                    class: "w-full",
                    inputmode: "url",
                    placeholder: "https://"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }, 8, ["label", "error"]),
              unref(showDietaryTags) ? (openBlock(), createBlock(UFormField, {
                key: 0,
                class: normalizeClass(["col-span-12", unref(showDeliveryFields) ? "sm:col-span-3" : "sm:col-span-6"]),
                label: unref(t)("products.dietary_tags")
              }, {
                default: withCtx(() => [
                  createVNode(USelectMenu, {
                    modelValue: dietaryTags.value,
                    "onUpdate:modelValue": _cache[34] || (_cache[34] = ($event) => dietaryTags.value = $event),
                    items: dietaryItems.value,
                    "value-key": "value",
                    multiple: "",
                    class: "w-full",
                    "search-input": false
                  }, null, 8, ["modelValue", "items"])
                ]),
                _: 1
              }, 8, ["class", "label"])) : createCommentVNode("", true),
              unref(showDeliveryFields) ? (openBlock(), createBlock(UFormField, {
                key: 1,
                class: normalizeClass(["col-span-12", unref(showDietaryTags) ? "sm:col-span-3" : "sm:col-span-6"]),
                label: unref(t)("products.alcohol_percentage"),
                error: errText("alcohol_percentage")
              }, {
                default: withCtx(() => [
                  createVNode(_sfc_main$2, {
                    modelValue: unref(form).data.alcohol_percentage,
                    "onUpdate:modelValue": _cache[35] || (_cache[35] = ($event) => unref(form).data.alcohol_percentage = $event),
                    precision: 2,
                    class: "w-full",
                    min: 0,
                    max: 100,
                    step: 0.1,
                    increment: false,
                    decrement: false
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }, 8, ["class", "label", "error"])) : createCommentVNode("", true),
              unref(showDeliveryFields) ? (openBlock(), createElementBlock("div", _hoisted_13, [
                createVNode(UCheckbox, {
                  modelValue: unref(form).data.excluded_from_delivery,
                  "onUpdate:modelValue": _cache[36] || (_cache[36] = ($event) => unref(form).data.excluded_from_delivery = $event),
                  label: unref(t)("products.excluded_from_delivery"),
                  description: unref(t)("products.excluded_from_delivery_description")
                }, null, 8, ["modelValue", "label", "description"])
              ])) : createCommentVNode("", true)
            ])
          ], 64)) : createCommentVNode("", true)
        ], 512), [
          [vShow, step.value === 1]
        ]),
        step.value === 2 ? (openBlock(), createElementBlock("div", _hoisted_14, [
          createVNode(UAlert, {
            color: "info",
            variant: "soft",
            icon: "i-lucide-info",
            description: unref(t)("products.no_quantity")
          }, null, 8, ["description"]),
          unref(inventoryAccountingEnabled) && ingredientsAlert.value ? (openBlock(), createBlock(UAlert, {
            key: 0,
            class: "mt-3",
            color: "primary",
            variant: "soft",
            icon: "i-lucide-circle-alert",
            close: true,
            "onUpdate:open": _cache[37] || (_cache[37] = ($event) => ingredientsAlert.value = false)
          }, {
            description: withCtx(() => [
              createElementVNode("div", null, [
                createElementVNode("strong", null, toDisplayString(unref(t)("products.purchase_price")), 1),
                createTextVNode(toDisplayString(unref(t)("products.ingredients.purchase_price_explanation")), 1)
              ]),
              createElementVNode("div", null, [
                createElementVNode("strong", null, toDisplayString(unref(t)("products.price_difference")), 1),
                createTextVNode(toDisplayString(unref(t)("products.ingredients.price_difference_explanation")), 1)
              ]),
              createElementVNode("div", _hoisted_15, [
                createElementVNode("strong", null, toDisplayString(unref(t)("products.selling_price")), 1),
                createTextVNode(toDisplayString(unref(t)("products.ingredients.selling_price_explanation")), 1)
              ]),
              createElementVNode("div", null, toDisplayString(unref(t)("general.note")) + ": " + toDisplayString(unref(t)("products.ingredients.note")), 1)
            ]),
            _: 1
          })) : createCommentVNode("", true),
          createVNode(UFormField, {
            class: "mt-4",
            label: unref(t)("products.select_ingredient", 1)
          }, {
            default: withCtx(() => [
              createVNode(_sfc_main$1, {
                modelValue: ingredientToAdd.value,
                "onUpdate:modelValue": _cache[38] || (_cache[38] = ($event) => ingredientToAdd.value = $event),
                items: selectableIngredients.value,
                "item-key": "id",
                "item-text": "name",
                "return-object": ""
              }, null, 8, ["modelValue", "items"])
            ]),
            _: 1
          }, 8, ["label"]),
          createElementVNode("div", _hoisted_16, [
            createVNode(UFormField, {
              class: "col-span-3",
              label: unref(t)("general.quantity")
            }, {
              default: withCtx(() => [
                withDirectives(createVNode(UInput, {
                  modelValue: ingredientQuantity.value,
                  "onUpdate:modelValue": _cache[39] || (_cache[39] = ($event) => ingredientQuantity.value = $event),
                  class: "w-full",
                  inputmode: "decimal",
                  disabled: !ingredientToAdd.value
                }, null, 8, ["modelValue", "disabled"]), [
                  [unref(maskito), unref(quantityMask)]
                ])
              ]),
              _: 1
            }, 8, ["label"]),
            createVNode(UFormField, {
              class: "col-span-3",
              label: unref(t)("general.multiplied_by")
            }, {
              default: withCtx(() => [
                withDirectives(createVNode(UInput, {
                  modelValue: ingredientMultiplier.value,
                  "onUpdate:modelValue": _cache[40] || (_cache[40] = ($event) => ingredientMultiplier.value = $event),
                  class: "w-full",
                  inputmode: "decimal",
                  disabled: !ingredientToAdd.value
                }, null, 8, ["modelValue", "disabled"]), [
                  [unref(maskito), unref(quantityMask)]
                ])
              ]),
              _: 1
            }, 8, ["label"]),
            createElementVNode("div", _hoisted_17, [
              ingredientToAdd.value && ingredientTotal.value ? (openBlock(), createElementBlock("span", _hoisted_18, toDisplayString(`= ${ingredientTotal.value.toFixed(3)} ${ingredientToAdd.value.unit_of_measure || ""}`), 1)) : createCommentVNode("", true)
            ]),
            createElementVNode("div", _hoisted_19, [
              ingredientToAdd.value && ingredientTotal.value ? (openBlock(), createBlock(UButton, {
                key: 0,
                color: "primary",
                variant: "ghost",
                size: "lg",
                icon: "i-lucide-plus",
                onClick: addIngredient
              }, {
                default: withCtx(() => [
                  createTextVNode(toDisplayString(unref(t)("general.add")), 1)
                ]),
                _: 1
              })) : createCommentVNode("", true)
            ])
          ]),
          createElementVNode("div", _hoisted_20, toDisplayString(unref(t)("general.ingredient", 2)), 1),
          createVNode(USeparator),
          (openBlock(true), createElementBlock(Fragment, null, renderList(selectedIngredients.value, (item, index) => {
            return openBlock(), createElementBlock("div", {
              key: item.product_id,
              class: normalizeClass(["flex items-center gap-2 py-1", { "border-b border-default": index !== selectedIngredients.value.length - 1 }])
            }, [
              createVNode(UButton, {
                color: "error",
                variant: "ghost",
                icon: "i-lucide-x",
                "aria-label": unref(t)("general.delete"),
                onClick: ($event) => removeIngredient(index)
              }, null, 8, ["aria-label", "onClick"]),
              createElementVNode("span", _hoisted_21, toDisplayString(item.name), 1),
              createElementVNode("span", _hoisted_22, toDisplayString(item.quantity) + " " + toDisplayString(item.unit), 1)
            ], 2);
          }), 128))
        ])) : createCommentVNode("", true),
        step.value === 1 ? (openBlock(), createElementBlock("div", {
          key: 1,
          class: normalizeClass(footerClass.value)
        }, [
          !unref(form).data.is_made ? (openBlock(), createBlock(UButton, {
            key: 0,
            size: unref(buttonSize),
            color: "neutral",
            variant: "outline",
            onClick: __props.onCancel
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(unref(t)("general.cancel")), 1)
            ]),
            _: 1
          }, 8, ["size", "onClick"])) : createCommentVNode("", true),
          !unref(form).data.is_made ? (openBlock(), createBlock(UButton, {
            key: 1,
            size: unref(buttonSize),
            color: "primary",
            variant: "solid",
            icon: "i-lucide-check",
            onClick: submit
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(unref(t)("general.save")), 1)
            ]),
            _: 1
          }, 8, ["size"])) : (openBlock(), createBlock(UButton, {
            key: 2,
            size: unref(buttonSize),
            color: "warning",
            variant: "soft",
            onClick: openIngredients
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(unref(t)("products.go_to_ingredients")), 1)
            ]),
            _: 1
          }, 8, ["size"]))
        ], 2)) : createCommentVNode("", true),
        step.value === 2 ? (openBlock(), createElementBlock("div", {
          key: 2,
          class: normalizeClass(footerClass.value)
        }, [
          createVNode(UButton, {
            size: unref(buttonSize),
            color: "neutral",
            variant: "outline",
            onClick: _cache[41] || (_cache[41] = ($event) => step.value = 1)
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(unref(t)("general.back")), 1)
            ]),
            _: 1
          }, 8, ["size"]),
          createVNode(UButton, {
            size: unref(buttonSize),
            color: "primary",
            variant: "solid",
            icon: "i-lucide-check",
            disabled: !selectedIngredients.value.length,
            onClick: submitWithIngredients
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(unref(t)("general.save")), 1)
            ]),
            _: 1
          }, 8, ["size", "disabled"])
        ], 2)) : createCommentVNode("", true)
      ]);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=index36.js.map
